import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AdminSidebar } from "./components/AdminSideBar";
import { ManageUserTab } from "./components/ManageUserTab/ManageUserTab";
import { UserSettingsTab } from "./components/UserSettingsTab/UserSettingsTab";
import { PromptLibrary } from "./components/PromptLibrary/PromptLibrary";
import { NotificationsTab } from "./components/NotificationsTab/NotificationsTab";

import { ModalConfirmWindow } from "./components/ManageUserTab/components/ModalConfirmWindow/ModalConfirmWindow";
import { CreateEditTeamModal } from "./components/CreateEditTeamModal/CreateEditTeamModal";

import { useDefaultFiltersContext } from "../../context/DefaultFiltersContext";
import { useUserContext } from "../../context/UserContext";

import { IRegulatorData, ITeam, IUserData } from "./interfaces/team.interface";

import { fetchDefaultFilterData } from "./services/defaultFilters.service";
import { fetchUserList } from "./services/fetchUserList.service";
import { fetchTeamList } from "./services/fetchTeamList.service";
import { fetchAllUserList } from "./services/fetchAllUserList.service";
import { fetchRegulatorList } from "./services/fetchRegulatorList.service";
import { fetchRightList } from "./services/fetchRightList.service";
import { fetchTeamData } from "./components/ManageUserTab/services/fetchTeamData.service";

import { createTeam } from "./services/createTeam.service";
import { addNewUser } from "./components/ManageUserTab/services/addNewUser.service";
import { deleteTeam } from "./services/deleteTeam.service";
import { updateTeam } from "./services/updateTeam.service";

import {
  transformRegulatorToOptions,
  transformRightsToOptions,
  transformToOptions,
} from "./helpers/transformToOptions";
import { canUserEdit } from "./helpers/userRights";

interface IAdminPanelPageProps {
  darkMode: boolean;
}

type SidebarTab = "manage_users" | "prompt_library" | "notifications" | "user_settings";

const AdminPanelPage: React.FC<IAdminPanelPageProps> = ({ darkMode }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<SidebarTab>("manage_users");
  const [teams, setTeams] = useState<ITeam[] | []>([]);
  const [selectedTeam, setSelectedTeam] = useState<ITeam | null>(null);
  const [editTeamId, setEditTeamId] = useState<number | null>(null);

  const [showModal, setShowModal] = useState<{
    isCreateTeam: boolean;
    visible: boolean;
    userIndex: number | null;
  }>({ isCreateTeam: false, visible: false, userIndex: null });

  const { defaultFilters, setDefaultFilters } = useDefaultFiltersContext();
  const { user, setUser } = useUserContext();

  // --- Queries ---
  const { data: defaultFilterData } = useQuery({
    queryKey: ["defaultFilterData"],
    queryFn: () => fetchDefaultFilterData(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: userRoleList = [] } = useQuery({
    queryKey: ["userRoleList"],
    queryFn: () => fetchUserList(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: teamList, isPending } = useQuery({
    queryKey: ["teamList"],
    queryFn: () => fetchTeamList(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: allUserList = [] } = useQuery({
    queryKey: ["allUserList"],
    queryFn: () => fetchAllUserList(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: teamData } = useQuery({
    queryKey: ["teamData", selectedTeam?.id],
    queryFn: () => fetchTeamData(selectedTeam?.id!),
    enabled: !!selectedTeam?.id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: editTeamData } = useQuery({
    queryKey: ["editTeamData", editTeamId],
    queryFn: () => fetchTeamData(editTeamId!),
    enabled: !!editTeamId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: regulatorList = [] } = useQuery({
    queryKey: ["regulatorList"],
    queryFn: () => fetchRegulatorList(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: rightList = [] } = useQuery({
    queryKey: ["rightList"],
    queryFn: () => fetchRightList(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // --- Mutations ---
  const { mutate: mutateUpdatedTeam } = useMutation({
    mutationFn: async ({ id, name, regIds, requestsLimit }: { id: number; name: string; regIds: (string | number)[]; requestsLimit?: number }) =>
      updateTeam(id, name, regIds, requestsLimit),
    onSuccess: () => toast.success(t("adminPanel.editTeamToaster", { defaultValue: "Team updated" })),
  });

  // --- Effects ---
  const queryClient = useQueryClient();
  useEffect(() => {
    if (defaultFilterData) setDefaultFilters(defaultFilterData);
  }, [defaultFilterData, setDefaultFilters]);

  useEffect(() => {
    if (teamList && teamList.length > 0) {
      setTeams(teamList);
      if (!user.is_superuser && !selectedTeam) setSelectedTeam(teamList[0]);
    }
  }, [teamList, isPending]); // eslint-disable-line

  useEffect(() => {
    const currentTab = location.pathname.split("/").pop() as SidebarTab | undefined;
    if (currentTab && ["manage_users", "prompt_library", "notifications", "user_settings"].includes(currentTab)) {
      setActiveTab(currentTab);
      if (currentTab === "user_settings") {
        window.dispatchEvent(new Event("userSettings:show"));
      }
    }
  }, [location.pathname]);

  // --- Handlers ---
  const handleTabChange = (tab: SidebarTab) => {
    setActiveTab(tab);
    navigate(`/admin_panel/${tab}`);
    if (tab === "user_settings") setSelectedTeam(null);
    if (tab === "user_settings") {
      window.dispatchEvent(new Event("userSettings:show"));
    }
  };

  const handleSelectTeam = (team: ITeam | null) => {
    setSelectedTeam(team);

    if (activeTab !== "prompt_library" && activeTab !== "notifications") {
      handleTabChange("manage_users");
    }
  };

  const openCreateTeamModal = () => {
    setEditTeamId(null);
    setShowModal({ visible: true, userIndex: null, isCreateTeam: true });
  };

  const openEditTeamModal = (team: ITeam) => {
    setEditTeamId(team.id);
    setShowModal({ visible: true, userIndex: team.id, isCreateTeam: true });
  };

  const openDeleteTeamModal = (team: ITeam) => {
    setShowModal({ visible: true, userIndex: team.id, isCreateTeam: false });
  };

  const handleDeleteTeam = () => {
    const id = showModal.userIndex;
    if (id == null) return;

    setTeams((prev) => prev.filter((t) => t.id !== id));
    toast.success(t("adminPanel.deleteTeamToaster", { defaultValue: "Team deleted" }));

    setShowModal({ isCreateTeam: false, visible: false, userIndex: null });
    deleteTeam(id);
  };

  const handleCreateEditTeam = (targetTeam: ITeam, isUpdate = false) => {
    const regIds = targetTeam.regulators.map((r: IRegulatorData) => r.id);

    if (isUpdate) {
      setTeams((prev) => prev.map((t) => (t.id === targetTeam.id ? targetTeam : t)));
      mutateUpdatedTeam({ id: targetTeam.id, name: targetTeam.name, regIds, requestsLimit: targetTeam.requests_limit });
    } else {
      // create team without roles so backend doesn't create default Viewer roles
      createTeam(targetTeam.name, regIds, [], targetTeam.requests_limit)
        .then(async (newTeam) => {
            if (newTeam && newTeam.id) {
            // If roles were provided, create Role entries with proper right_id via /roles/
            try {
              const roles = Array.isArray(targetTeam.roles) ? targetTeam.roles : [];
              const items = roles
                .filter((r: any) => r?.user?.email)
                .map((r: any) => ({
                  name: r.user.name || null,
                  email: r.user.email,
                  right_id: r.right_id ?? 3,
                  team_id: newTeam.id,
                }));

              if (items.length > 0) {
                await addNewUser(items);
                // Invalidate team queries so UI shows updated roles
                try {
                  queryClient.invalidateQueries({ queryKey: ["teamList"] });
                  queryClient.invalidateQueries({ queryKey: ["teamData", newTeam.id] });
                } catch (e) {
                  // ignore
                }
              }
            } catch (err) {
              console.error("Error creating roles for new team:", err);
            }

            setTeams((prev) => [...prev, newTeam]);
            toast.success(t("adminPanel.createTeamToaster", { defaultValue: "Team created" }));
          }
        })
        .catch((err) => {
          console.error("Error creating team:", err);
        });
    }

    setShowModal({ isCreateTeam: true, visible: false, userIndex: null });
  };

  return (
    <div className="flex flex-1 min-h-0 h-[calc(100vh-48px)] overflow-hidden">
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        user={user}
        teams={teams}
        selectedTeam={selectedTeam}
        onSelectTeam={handleSelectTeam}
        selectedTeamRegulators={teamData?.regulators}
        onCreateTeam={openCreateTeamModal}
        onEditTeam={openEditTeamModal}
        onDeleteTeam={openDeleteTeamModal}
      />

      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        <div className={`${activeTab === "manage_users" ? "block h-full overflow-auto" : "hidden"}`}>
          <ManageUserTab
            darkMode={darkMode}
            teams={transformToOptions(teams.map((team) => team.name))}
            userRoleList={selectedTeam ?? userRoleList}
            teamId={selectedTeam ? selectedTeam.id : null}
            teamList={(Array.isArray(teamList) ? teamList : []).filter((team: ITeam) => canUserEdit(user, team))}
            isSelectedTeam={!!selectedTeam}
            selectedTeam={selectedTeam}
            allUserList={allUserList}
            userList={(allUserList || []).map((u: IUserData) => ({
              value: u.email,
              label: u.name === "" ? u.email : `${u.name} <${u.email}>`,
            }))}
            roleOptions={transformRightsToOptions(rightList)}
            activeTab={activeTab}
          />
        </div>

        <div className={`${activeTab === "user_settings" ? "block h-full overflow-hidden" : "hidden"}`}>
          <UserSettingsTab defaultFilters={defaultFilters} user={user} setUser={setUser} />
        </div>

        <div className={`${activeTab === "prompt_library" ? "block h-full overflow-auto" : "hidden"}`}>
          <PromptLibrary
            darkMode={darkMode}
            selectedTeam={selectedTeam}
            teamOptions={(Array.isArray(teamList) ? teamList : [])
              .filter((team: ITeam) => canUserEdit(user, team))
              .map((team: ITeam) => ({ value: String(team.id), label: team.name }))}
            activeTab={activeTab}
          />
        </div>

        <div className={`${activeTab === "notifications" ? "block h-full overflow-auto" : "hidden"}`}>
          <NotificationsTab
            darkMode={darkMode}
            teams={transformToOptions(teams.map((team) => team.name))}
            teamId={selectedTeam ? selectedTeam.id : null}
            teamList={(Array.isArray(teamList) ? teamList : []).filter((team: ITeam) => canUserEdit(user, team))}
            allTeamList={Array.isArray(teamList) ? teamList : []}
            teamOptions={(Array.isArray(teamList) ? teamList : [])
              .filter((team: ITeam) => canUserEdit(user, team))
              .map((team: ITeam) => ({ value: String(team.id), label: team.name }))}
            isSelectedTeam={!!selectedTeam}
            selectedTeam={selectedTeam}
            activeTab={activeTab}
          />
        </div>
      </main>

      {/* Delete Team confirm */}
      {showModal.visible && !showModal.isCreateTeam && (
        <ModalConfirmWindow
          setShowModal={setShowModal}
          handleDeleteUser={handleDeleteTeam}
          target="team"
        />
      )}

      {/* Create/Edit Team */}
      {showModal.visible && showModal.isCreateTeam && (!showModal.userIndex || editTeamData) && (
        <CreateEditTeamModal
          teamData={showModal.userIndex !== null ? editTeamData : null}
          setShowModal={(visible: boolean) =>
            setShowModal({ visible, userIndex: 0, isCreateTeam: true })
          }
          handleCreateEditTeam={handleCreateEditTeam}
          adminOptions={allUserList.map((u: IUserData) => ({
            value: u.email,
            label: u.name === "" ? u.email : `${u.name} <${u.email}>`,
          }))}
          memberOptions={allUserList.map((u: IUserData) => ({
            value: u.email,
            label: u.name === "" ? u.email : `${u.name} <${u.email}>`,
          }))}
          regulatorOptions={transformRegulatorToOptions(regulatorList)}
          teamOptions={(Array.isArray(teamList) ? teamList : []).map((team: ITeam) => ({
            value: String(team.id),
            label: team.name,
            members: (team.roles || []).map((r: any) => ({ value: r.user.email, label: r.user.name || r.user.email })),
          }))}
          roleOptions={transformRightsToOptions(rightList)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export { AdminPanelPage };